import { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { CreditCard, Loader2, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubscribePage() {
  const { t } = useTranslation();
  const { isAuthenticated, loginWithRedirect, getAccessTokenSilently } = useAuth0();
  const [searchParams] = useSearchParams();
  const [plan, setPlan] = useState('monthly');
  const [provider, setProvider] = useState('stripe');
  const [loading, setLoading] = useState(false);
  const [sub, setSub] = useState(null);
  const [subLoading, setSubLoading] = useState(true);
  const [canceling, setCanceling] = useState(false);

  const isSuccess = searchParams.has('session_id') || searchParams.get('paypal') === 'true';
  const isCanceled = window.location.pathname.includes('otkazano');

  const loadSubscription = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const r = await fetch(`${API}/subscriptions/me`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setSub(await r.json());
    } catch (e) { console.error(e); }
    finally { setSubLoading(false); }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (isAuthenticated) loadSubscription();
    else setSubLoading(false);
  }, [isAuthenticated, loadSubscription]);

  async function handleCheckout() {
    if (!isAuthenticated) { loginWithRedirect({ appState: { returnTo: '/pretplata' } }); return; }
    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const endpoint = provider === 'stripe' ? '/subscriptions/stripe/checkout' : '/subscriptions/paypal/create-subscription';
      const r = await fetch(`${API}${endpoint}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan }),
      });
      if (r.ok) {
        const data = await r.json();
        const url = data.url || data.approval_url;
        if (url) window.location.href = url;
        else toast.error('Nema URL-a za plaćanje');
      } else { const e = await r.json().catch(() => ({})); toast.error(e.detail || 'Greška'); }
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }

  async function handleCancel() {
    if (!window.confirm('Jeste li sigurni da želite otkazati pretplatu?')) return;
    setCanceling(true);
    try {
      const token = await getAccessTokenSilently();
      await fetch(`${API}/subscriptions/cancel`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      toast.success('Pretplata otkazana');
      loadSubscription();
    } catch (e) { toast.error(e.message); }
    finally { setCanceling(false); }
  }

  if (isSuccess) return (
    <div className="max-w-xl mx-auto px-4 py-16 text-center" data-testid="subscribe-success">
      <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4"><Check size={32} className="text-emerald-600" /></div>
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">Hvala na pretplati!</h1>
      <p className="text-gray-500 mb-6">Vaša pretplata je aktivna. Uživajte u svim premium sadržajima.</p>
      <Link to="/" className="inline-flex px-6 py-3 bg-slate-900 text-white rounded-lg font-medium text-sm">Nazad na početnu</Link>
    </div>
  );

  if (isCanceled) return (
    <div className="max-w-xl mx-auto px-4 py-16 text-center" data-testid="subscribe-canceled">
      <div className="w-16 h-16 bg-gray-100 dark:bg-[#111827] rounded-full flex items-center justify-center mx-auto mb-4"><X size={32} className="text-gray-400" /></div>
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">Pretplata otkazana</h1>
      <p className="text-gray-500 mb-6">Možete se pretplatiti bilo kada.</p>
      <Link to="/pretplata" className="inline-flex px-6 py-3 bg-slate-900 text-white rounded-lg font-medium text-sm">Pokušaj ponovo</Link>
    </div>
  );

  // Active subscription management
  if (sub && sub.status === 'active') return (
    <>
      <Helmet><title>{`Upravljanje pretplatom \u2014 e-mina.eu`}</title></Helmet>
      <div className="max-w-xl mx-auto px-4 py-12" data-testid="manage-subscription">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6 text-center">Upravljanje pretplatom</h1>
        <div className="bg-white dark:bg-[#111827] rounded-xl border border-gray-200 dark:border-gray-800 p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm text-gray-500">Plan</span>
            <span className="font-semibold">{sub.plan === 'yearly' ? 'Godišnja' : 'Mjesečna'} pretplata</span>
          </div>
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm text-gray-500">Provider</span>
            <span className="font-semibold capitalize">{sub.provider}</span>
          </div>
          {sub.current_period_end && (
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-gray-500">Vrijedi do</span>
              <span className="font-semibold">{new Date(sub.current_period_end).toLocaleDateString('hr')}</span>
            </div>
          )}
          {sub.cancel_at_period_end && <p className="text-sm text-amber-600 mb-4">Pretplata će biti otkazana na kraju perioda.</p>}
          {!sub.cancel_at_period_end && (
            <button onClick={handleCancel} disabled={canceling} className="w-full py-2.5 border border-red-200 text-red-600 rounded-lg text-sm hover:bg-red-50" data-testid="cancel-sub-btn">
              {canceling ? 'Otkaz u tijeku...' : 'Otkaži pretplatu'}
            </button>
          )}
        </div>
      </div>
    </>
  );

  return (
    <>
      <Helmet><title>{`${t('subscribe.title')} \u2014 e-mina.eu`}</title></Helmet>
      <div className="max-w-4xl mx-auto px-4 py-12" data-testid="subscribe-page">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">{t('subscribe.title')}</h1>
          <p className="text-gray-500">{t('subscribe.subtitle')}</p>
        </div>
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Monthly */}
          <button onClick={() => setPlan('monthly')} className={`text-left border-2 rounded-xl p-6 transition-all ${plan === 'monthly' ? 'border-red-500 ring-1 ring-red-200' : 'border-gray-200 dark:border-gray-800 hover:border-gray-300'}`} data-testid="plan-monthly">
            <h2 className="text-lg font-bold mb-1">Mjesečna pretplata</h2>
            <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">4,99 <span className="text-base font-normal text-gray-500">€/mj.</span></p>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-center gap-2"><Check size={14} className="text-emerald-500" /> Bez oglasa</li>
              <li className="flex items-center gap-2"><Check size={14} className="text-emerald-500" /> Svi premium članci</li>
            </ul>
          </button>
          {/* Yearly */}
          <button onClick={() => setPlan('yearly')} className={`text-left border-2 rounded-xl p-6 transition-all relative ${plan === 'yearly' ? 'border-red-500 ring-1 ring-red-200' : 'border-gray-200 dark:border-gray-800 hover:border-gray-300'}`} data-testid="plan-yearly">
            <span className="absolute -top-3 right-4 bg-emerald-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold uppercase">Uštedi 17%</span>
            <h2 className="text-lg font-bold mb-1">Godišnja pretplata</h2>
            <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">49,99 <span className="text-base font-normal text-gray-500">€/god.</span></p>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-center gap-2"><Check size={14} className="text-emerald-500" /> Bez oglasa</li>
              <li className="flex items-center gap-2"><Check size={14} className="text-emerald-500" /> Svi premium članci</li>
              <li className="flex items-center gap-2"><Check size={14} className="text-emerald-500" /> Ušteda od 17% godišnje</li>
            </ul>
          </button>
        </div>
        {/* Provider */}
        <div className="bg-gray-50 dark:bg-[#0b1220] rounded-xl p-5 mb-6">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">Način plaćanja:</p>
          <div className="flex gap-3">
            <label className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border cursor-pointer transition-all ${provider === 'stripe' ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-200 dark:border-blue-400' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-[#0f172a] text-gray-800 dark:text-gray-100'}`}>
              <input type="radio" name="provider" value="stripe" checked={provider === 'stripe'} onChange={() => setProvider('stripe')} className="sr-only" />
              <CreditCard size={16} /> <span className="text-sm font-medium">Kartica (Stripe)</span>
            </label>
            <label className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border cursor-pointer transition-all ${provider === 'paypal' ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-200 dark:border-blue-400' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-[#0f172a] text-gray-800 dark:text-gray-100'}`}>
              <input type="radio" name="provider" value="paypal" checked={provider === 'paypal'} onChange={() => setProvider('paypal')} className="sr-only" />
              <span className="text-sm font-medium">PayPal</span>
            </label>
          </div>
        </div>
        <button onClick={handleCheckout} disabled={loading || subLoading} className="w-full py-3.5 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl text-base transition-colors disabled:opacity-50 flex items-center justify-center gap-2" data-testid="checkout-btn">
          {loading ? <><Loader2 className="animate-spin" size={18} /> Preusmjeravanje...</> : 'Pretplati se'}
        </button>
        {!isAuthenticated && <p className="text-center text-sm text-gray-400 mt-3">Trebate se prijaviti za pretplatu</p>}
      </div>
    </>
  );
}
